import cx from "classnames";
import { FancyCheckmark } from "common/FancyCheckmark";
import { messages as globalFooterMessages } from "common/GlobalFooter";
import { Icon } from "common/Icon";
import { loginMessages } from "common/styleguide/LoginModal";
import {
  PopoverLinkMenu,
  PopoverLinkMenuItem as LinkMenuItem,
  PopoverMenuDivider as Divider,
} from "common/styleguide/PopoverLinkMenu";
import { PopoverMenuItem as MenuItem } from "common/styleguide/PopoverMenu";
import fetchJSON from "lib/fetchJSON";
import { commonCTAs, commonVerbs, featureNames, useFormatMessage } from "lib/localization";
import { errorMessage } from "lib/styleguide/base.scss";
import { getCurrentUser } from "lib/utils";
import React, { useEffect, useReducer, useRef, useState } from "react";
import { defineMessages } from "react-intl";
import styles from "./HelpPopover.scss";
import { hotkeys, tips } from "./tips";

const Base = ({ dispatch, planner, fm }) => {
  return (
    <>
      <MenuItem className={styles.blue}>RWGPS {fm(featureNames.help)}</MenuItem>
      {planner && (
        <>
          <MenuItem className={styles.arrowItem} onClick={() => dispatch(actions.showTips)}>
            {fm(messages.plannerTips)} <Icon i="chevron-centered" size={16} />
          </MenuItem>
          <MenuItem className={styles.arrowItem} onClick={() => dispatch(actions.showShortcuts)}>
            {fm(messages.kbShortcuts)} <Icon i="chevron-centered" size={16} />
          </MenuItem>
        </>
      )}
      <LinkMenuItem target="_blank" to="/help">
        {fm(globalFooterMessages.helpCenter)}
      </LinkMenuItem>
      <LinkMenuItem target="_blank" to="/faq">
        {fm(featureNames.faq)}
      </LinkMenuItem>
      <Divider />
      <LinkMenuItem onClick={() => dispatch(actions.showContact)}>{fm(messages.contactSupport)}</LinkMenuItem>
    </>
  );
};

const Tips = ({ dispatch, fm }) => {
  const [tip, setTip] = useState(0);
  const increment = () => {
    setTip((tip + 1) % tips.length);
  };

  return (
    <>
      <CenteredHeader action={() => dispatch(actions.showBase)} message={fm(messages.plannerTips)} />
      <MenuItem className={styles.tip}>{fm(tips[tip].tip)}</MenuItem>
      <MenuItem>
        <a target="_blank" className={styles.orangeLink} href={tips[tip].link}>
          {fm(commonCTAs.learnMore)}
        </a>
      </MenuItem>
      <Divider />
      <LinkMenuItem className={styles.rightAlign} onClick={increment}>
        {fm(messages.showNextTip)}
      </LinkMenuItem>
    </>
  );
};

const formatHotkey = ([shortcut, label], fm) => {
  if (!label) {
    return (
      <div key={shortcut.id} className={styles.label}>
        {fm(shortcut)}
      </div>
    );
  } else {
    const shortcutLabel = (typeof shortcut === "string" ? shortcut : shortcut[0]).replace(
      /mod/,
      window.navigator.platform.match(/Mac/) ? "cmd" : "ctrl"
    );
    return (
      <div key={label.id}>
        <span>{fm(label)}</span> <code>{shortcutLabel}</code>
      </div>
    );
  }
};

const Shortcuts = ({ dispatch, fm }) => (
  <>
    <CenteredHeader action={() => dispatch(actions.showBase)} message={fm(messages.kbShortcuts)} />
    <MenuItem className={styles.hotkeys}>
      <div className={styles.col}>{hotkeys[0].map((k) => formatHotkey(k, fm))}</div>

      <div className={styles.col}>{hotkeys[1].map((k) => formatHotkey(k, fm))}</div>
    </MenuItem>
  </>
);

const initializeName = () => {
  const currentUser = getCurrentUser();
  return `${currentUser.firstName} ${currentUser.lastName} (ID ${currentUser.id})`;
};

const Contact = ({ dispatch, fm }) => {
  const [sending, setSending] = useState(false);
  const [name, setName] = useState(initializeName());
  const [email, setEmail] = useState(null);
  const [comment, setComment] = useState(null);
  const [error, setError] = useState({ email: false, comment: false });

  const submit = () => {
    if (sending) return;
    setSending(true);

    const nextError = {
      email: !email,
      comment: !comment,
    };
    if (Object.values(nextError).some((v) => v)) {
      setError(nextError);
    }

    const body = {
      request: {
        requester: {
          name,
          email,
        },
        comment,
        location: window.location.href,
      },
    };

    fetchJSON("/create_ticket.json", body, { method: "POST" }).then((r) => {
      setSending(false);
      dispatch(actions.showSuccess);
    });
  };

  return (
    <>
      <CenteredHeader action={() => dispatch(actions.showBase)} message={fm(messages.leaveMessage)} />
      <MenuItem>
        <form className={styles.supportForm} onSubmit={submit}>
          <label>{fm(messages.yourName)}</label>
          <input defaultValue={name} onChange={(e) => setName(e.target.value)} readOnly />
          <label>{fm(loginMessages.email)}</label>
          <input
            type="email"
            defaultValue={email}
            onChange={(e) => setEmail(e.target.value)}
            className={error.email ? errorMessage : undefined}
            required
          />
          <label>{fm(messages.howHelp)}</label>
          <textarea
            rows={5}
            defaultValue={comment}
            onChange={(e) => setComment(e.target.value)}
            className={error.comment ? errorMessage : undefined}
            required
          />
        </form>
      </MenuItem>
      <Divider />
      <MenuItem className={styles.send}>
        <a disabled={sending} onClick={submit}>
          {fm(commonVerbs.send)}
        </a>
      </MenuItem>
    </>
  );
};

// this is a different component so we can force a rerender of the popover (so the frame doesn't drift from the arrow)
const Success = ({ dispatch, fm }) => (
  <>
    <CenteredHeader action={() => dispatch(actions.showBase)} message={fm(messages.messageSent)} />
    <MenuItem className={styles.success}>
      <FancyCheckmark />
      <h4>{fm(messages.thanks)}</h4>
      {fm(messages.getBack)}
      <a onClick={() => dispatch(actions.showBase)}>{fm(commonCTAs.goBack)}</a>
    </MenuItem>
  </>
);

const CenteredHeader = ({ action, message }) => (
  <MenuItem className={cx(styles.blue, styles.arrowItem, styles.left)} onClick={action}>
    <Icon i="chevron-centered" size={16} />
    {message}
    <span style={{ width: "16px" }} /> {/* layout hack */}
  </MenuItem>
);

// this isn't at all necessary, i just felt like trying it out
const states = {
  base: { component: Base, maxWidth: "200px" },
  tips: { component: Tips, maxWidth: "230px" },
  shortcuts: { component: Shortcuts, maxWidth: "320px" },
  contact: { component: Contact, maxWidth: "420px" },
  success: { component: Success, maxWidth: "420px" },
};

const initialState = states.base;

const reducer = (state, action) => {
  switch (action.type) {
    case "showTips":
      return states.tips;
    case "showShortcuts":
      return states.shortcuts;
    case "showContact":
      return states.contact;
    case "showSuccess":
      return states.success;
    default:
      return states.base;
  }
};

const actions = {
  showTips: { type: "showTips" },
  showShortcuts: { type: "showShortcuts" },
  showContact: { type: "showContact" },
  showSuccess: { type: "showSuccess" },
  showBase: { type: "showBase" },
};

const messages = defineMessages({
  plannerTips: {
    id: "helpPopover.plannerTips",
    defaultMessage: "Planner Tips",
  },
  kbShortcuts: {
    id: "helpPopover.kbShortcuts",
    defaultMessage: "Keyboard Shortcuts",
  },
  contactSupport: {
    id: "helpPopover.contactSupport",
    defaultMessage: "Contact Support",
  },
  showNextTip: {
    id: "helpPopover.showNextTip",
    defaultMessage: "Show Next Tip",
  },
  leaveMessage: {
    id: "helpPopover.leaveMessage",
    defaultMessage: "Leave us a message",
  },
  messageSent: {
    id: "helpPopover.messageSent",
    defaultMessage: "Message sent",
  },
  thanks: {
    id: "helpPopover.thanks",
    defaultMessage: "Thanks for reaching out",
  },
  getBack: {
    id: "helpPopover.getBack",
    defaultMessage: "Someone will get back to you soon",
  },
  yourName: {
    id: "helpPopover.yourName",
    defaultMessage: "Your name",
  },
  howHelp: {
    id: "helpPopover.howHelp",
    defaultMessage: "How can we help you?",
  },
});

const onPlanner = (pathname) => {
  return pathname.startsWith("/routes/new") || !!pathname.match(/\/(routes)\/\d+\/(edit)/);
};

export const HelpPopover = ({ intl }) => {
  const triggerRef = useRef(null);
  const containerRef = useRef(null);
  const fm = useFormatMessage();
  const [show, setShow] = useState(false);
  const [planner, setPlanner] = useState(onPlanner(window.location.pathname));
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    setPlanner(onPlanner(window.location.pathname));
  }, [window.location.pathname]);

  const onRequestClose = () => {
    dispatch(actions.showBase);
    setShow(false);
  };

  const resideAndDispatch = (action) => {
    // resize to force react-overlay to recalculate containerPadding
    window.dispatchEvent(new Event("resize")); // eslint-disable-line
    dispatch(action);
  };

  const frameProps = { dispatch: resideAndDispatch, planner, fm };

  return (
    <div ref={containerRef}>
      <Icon i="help-icon" size={36} ref={triggerRef} className={styles.helpIcon} onClick={() => setShow(true)} />
      {show && (
        <PopoverLinkMenu
          maxWidth={state.maxWidth}
          onRequestClose={onRequestClose}
          placement="top"
          target={triggerRef}
          container={containerRef}
          containerPadding={15}
          className={styles.blueTheme}
        >
          <state.component {...frameProps} />
        </PopoverLinkMenu>
      )}
    </div>
  );
};
